import { useTranslation } from 'react-i18next';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import WhatsAppFloat from '../../home/components/WhatsAppFloat';
import SEO from '@/components/SEO';

export default function EducationPage() {
  const { t } = useTranslation('navigation');
  const isEn = t('home') === 'Home';

  const solutions = [
    {
      titleFr: 'Gestion des Étudiants', titleEn: 'Student Management',
      descFr: 'Dossiers étudiants complets avec suivi académique personnalisé', descEn: 'Complete student files with personalized academic tracking',
      icone: 'ri-user-line',
      featuresFr: ['Dossiers étudiants numériques', 'Suivi des inscriptions', 'Historique académique complet', 'Gestion des absences', 'Communication parents-école'],
      featuresEn: ['Digital student records', 'Enrollment tracking', 'Complete academic history', 'Absence management', 'Parent-school communication'],
    },
    {
      titleFr: 'Planning & Emplois du Temps', titleEn: 'Scheduling & Timetables',
      descFr: 'Planification automatisée des cours et gestion des salles', descEn: 'Automated lesson planning and classroom management',
      icone: 'ri-calendar-line',
      featuresFr: ['Génération automatique des emplois du temps', 'Gestion des salles de classe', 'Planning des enseignants', 'Réservation des ressources', 'Notifications de changements'],
      featuresEn: ['Automatic timetable generation', 'Classroom management', 'Teacher scheduling', 'Resource booking', 'Change notifications'],
    },
    {
      titleFr: 'Gestion des Notes', titleEn: 'Grade Management',
      descFr: 'Saisie et calcul automatique des notes et moyennes', descEn: 'Entry and automatic calculation of grades and averages',
      icone: 'ri-file-text-line',
      featuresFr: ['Saisie des notes en ligne', 'Calcul automatique des moyennes', 'Bulletins de notes numériques', 'Suivi des progressions', 'Analyses statistiques des résultats'],
      featuresEn: ['Online grade entry', 'Automatic average calculation', 'Digital report cards', 'Progress tracking', 'Statistical result analysis'],
    },
    {
      titleFr: 'Facturation & Comptabilité', titleEn: 'Billing & Accounting',
      descFr: "Gestion financière complète de l'établissement", descEn: 'Complete financial management of the institution',
      icone: 'ri-calculator-line',
      featuresFr: ['Facturation automatique des frais', 'Suivi des paiements', 'Gestion des bourses', 'Comptabilité intégrée', 'Rapports financiers détaillés'],
      featuresEn: ['Automatic fee billing', 'Payment tracking', 'Scholarship management', 'Integrated accounting', 'Detailed financial reports'],
    },
    {
      titleFr: 'Bibliothèque Numérique', titleEn: 'Digital Library',
      descFr: 'Gestion complète des ressources documentaires', descEn: 'Complete management of documentary resources',
      icone: 'ri-book-line',
      featuresFr: ['Catalogue numérique des livres', 'Système de prêt automatisé', 'Recherche avancée', 'Réservations en ligne', "Statistiques d'utilisation"],
      featuresEn: ['Digital book catalog', 'Automated lending system', 'Advanced search', 'Online reservations', 'Usage statistics'],
    },
    {
      titleFr: 'Portail Parents', titleEn: 'Parent Portal',
      descFr: 'Interface dédiée pour le suivi parental', descEn: 'Dedicated interface for parental monitoring',
      icone: 'ri-parent-line',
      featuresFr: ['Consultation des notes en temps réel', 'Suivi des absences', 'Communication avec les enseignants', 'Calendrier des événements', 'Paiements en ligne'],
      featuresEn: ['Real-time grade consultation', 'Absence tracking', 'Communication with teachers', 'Event calendar', 'Online payments'],
    },
  ];

  const advantages = [
    { icon: 'ri-smartphone-line', titleKey: 'educationAdv1Title', descKey: 'educationAdv1Desc' },
    { icon: 'ri-time-line', titleKey: 'educationAdv2Title', descKey: 'educationAdv2Desc' },
    { icon: 'ri-user-star-line', titleKey: 'educationAdv3Title', descKey: 'educationAdv3Desc' },
    { icon: 'ri-team-line', titleKey: 'educationAdv4Title', descKey: 'educationAdv4Desc' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Logiciel Éducation - Alpha Solutions | Gestion Scolaire & Université"
        description="Solution complète pour écoles et universités : gestion des étudiants, emplois du temps, notes, facturation, bibliothèque numérique et portail parents."
        keywords="logiciel école Cameroun, gestion scolaire Douala, software éducation Afrique, système université"
        canonicalPath="/secteurs/education"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Logiciel Éducation Alpha Solutions",
          "description": "Solution de gestion scolaire et universitaire au Cameroun",
          "brand": { "@type": "Organization", "name": "Alpha Solutions" }
        }}
      />
      <Header />
      
      <section className="relative bg-cover bg-center py-20"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6),rgba(0,0,0,0.6)), url('https://readdy.ai/api/search-image?query=modern%20african%20educational%20institution%20with%20digital%20student%20management%20system%20and%20contemporary%20learning%20technology%2C%20professional%20school%20environment%20with%20advanced%20educational%20technology%20solutions&width=1200&height=600&seq=education-hero&orientation=landscape')` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">{t('educationHeroTitle')}</h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">{t('educationHeroDesc')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://wa.me/237693385267" target="_blank" rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-whatsapp-line mr-2"></i>{t('sectorDemoBtn')}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('educationSolutionsTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('educationSolutionsSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((sol, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mb-4">
                  <i className={`${sol.icone} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{isEn ? sol.titleEn : sol.titleFr}</h3>
                <p className="text-gray-600 mb-4">{isEn ? sol.descEn : sol.descFr}</p>
                <ul className="space-y-2">
                  {(isEn ? sol.featuresEn : sol.featuresFr).map((f, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                      <span className="text-sm text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('educationWhyTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((adv, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${adv.icon} text-2xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t(adv.titleKey)}</h3>
                <p className="text-gray-600">{t(adv.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 rounded-lg p-8 border-l-4 border-blue-500">
            <div className="flex items-center mb-6">
              <img src="https://readdy.ai/api/search-image?query=professional%20african%20school%20director%20in%20modern%20educational%20institution%2C%20confident%20education%20administrator%20managing%20successful%20school%20with%20digital%20technology%20solutions&width=100&height=100&seq=groupe-scolaire-etoile&orientation=squarish"
                alt={t('educationTestiName')} className="w-16 h-16 rounded-full object-cover mr-4" />
              <div>
                <h3 className="text-xl font-bold text-blue-900">{t('educationTestiName')}</h3>
                <p className="text-blue-700">{t('educationTestiRole')}</p>
              </div>
            </div>
            <blockquote className="text-lg text-blue-800 italic mb-4">"{t('educationTestiQuote')}"</blockquote>
            <div className="flex items-center">
              <div className="flex text-yellow-400">{[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill"></i>)}</div>
              <span className="ml-2 text-blue-700 font-medium">{t('educationSatisfied')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-blue-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('educationCtaTitle')}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">{t('educationCtaDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/237693385267" target="_blank" rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line mr-2"></i>{t('sectorFreeDemo')}
            </a>
            <a href="/solutions" className="bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
              {t('sectorViewSolutions')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
